import { Platform, isNumber } from '@tyler-components-web/core';
import { TAB_BAR_CONSTANTS, ACCEPTABLE_KEYS, KEYCODE_MAP } from './tab-bar-constants';
import { TAB_CONSTANTS } from '../tab/tab-constants';
var TabBarFoundation = /** @class */ (function () {
    function TabBarFoundation(_adapter) {
        var _this = this;
        this._adapter = _adapter;
        this._isInitialized = false;
        this._activeTab = -1;
        this._layoutMode = 'fixed';
        this._layoutAlign = 'start';
        this._underline = false;
        this._focusOnActivate = true;
        this._autoActivate = false;
        this._stacked = false;
        this._scrollButtons = true;
        this._forceScrollButtons = false;
        this._tabsChangedListener = function (evt) { return _this._onTabsChanged(evt); };
        this._tabInteractedListener = function (evt) { return _this._onTabInteracted(evt); };
        this._keydownListener = function (evt) { return _this._onKeydown(evt); };
        this._scrollPrevClickListener = function (evt) { return _this._onScrollPrevious(evt); };
        this._scrollPrevMousedownListener = function (evt) { return evt.preventDefault(); };
        this._scrollNextClickListener = function (evt) { return _this._onScrollNext(evt); };
        this._scrollNextMousedownListener = function (evt) { return evt.preventDefault(); };
        this._scrollListener = function (evt) { return _this._onScroll(evt); };
    }
    TabBarFoundation.prototype.initialize = function () {
        var _this = this;
        this._adapter.initializeTabs(this._activeTab, this._isFixed);
        this._adapter.initializeTabScroller();
        this._adapter.addTabChangeListener(this._tabsChangedListener);
        this._adapter.addScrollPreviousListener('click', this._scrollPrevClickListener);
        this._adapter.addScrollPreviousListener('mousedown', this._scrollPrevMousedownListener);
        this._adapter.addScrollNextListener('click', this._scrollNextClickListener);
        this._adapter.addScrollNextListener('mousedown', this._scrollNextMousedownListener);
        this._adapter.addHostListener(TAB_CONSTANTS.events.INTERACTED, this._tabInteractedListener);
        this._adapter.addHostListener('keydown', this._keydownListener);
        this._adapter.addScrollListener(this._scrollListener);
        this._adapter.setUnderline(this._underline);
        this._adapter.setLayoutMode(this._layoutMode);
        this._adapter.setLayoutAlign(this._layoutAlign);
        this._adapter.setFocusOnActivate(this._focusOnActivate);
        window.requestAnimationFrame(function () {
            _this._syncScrollButtons();
            _this._adapter.ensureFocusableTab();
        });
        this._isInitialized = true;
    };
    TabBarFoundation.prototype.disconnect = function () {
        this._adapter.destroyTabScroller();
        this._adapter.removeTabChangeListener(this._tabsChangedListener);
        this._adapter.removeHostListener(TAB_CONSTANTS.events.INTERACTED, this._tabInteractedListener);
        this._adapter.removeHostListener('keydown', this._keydownListener);
        this._isInitialized = false;
    };
    TabBarFoundation.prototype._onTabsChanged = function (evt) {
        var _this = this;
        this._adapter.initializeTabs(this._activeTab, this._isFixed);
        this._adapter.ensureFocusableTab();
        // We need to wait a full cycle to scroll the active tab into view
        window.setTimeout(function () {
            window.requestAnimationFrame(function () {
                _this.scrollIntoView(_this._activeTab);
                _this._syncScrollButtons();
            });
        });
    };
    TabBarFoundation.prototype._onTabInteracted = function (evt) {
        var activatedTab = evt.target;
        var activatedTabIndex = this._adapter.getTabIndex(activatedTab);
        // Check if the event and requested tab index is valid
        var isTabEvent = activatedTab.tagName.toLowerCase() === TAB_CONSTANTS.elementName;
        var isValidTabIndex = activatedTabIndex !== this._activeTab && this._indexIsInRange(activatedTabIndex);
        if (!isTabEvent || !isValidTabIndex) {
            return;
        }
        // Emit the activate event
        var activateEventData = { index: activatedTabIndex };
        var canActivate = this._adapter.emitHostEvent(TAB_BAR_CONSTANTS.events.ACTIVATE, activateEventData, true, true);
        // Check if listener prevented default on the activate event, and stop now if so
        if (!canActivate) {
            return;
        }
        this.activateTab(activatedTabIndex);
    };
    TabBarFoundation.prototype._onKeydown = function (evt) {
        var key = this._getKeyFromEvent(evt);
        // Early exit if the event key isn't one of the keyboard navigation keys
        if (!key) {
            return;
        }
        // Prevent default behavior for movement keys, but not for activation keys, since :active is used to apply ripple
        if (!this._isActivationKey(key)) {
            evt.preventDefault();
        }
        if (this._autoActivate) {
            if (this._isActivationKey(key)) {
                return;
            }
            var index = this._determineTargetFromKey(this._activeTab, key);
            if (this._activeTab === index) {
                return;
            }
            var activateEventData = { index: index };
            var canActivate = this._adapter.emitHostEvent(TAB_BAR_CONSTANTS.events.ACTIVATE, activateEventData, true, true);
            if (canActivate) {
                this.activateTab(index);
                if (this._adapter.canFocusTab(index)) {
                    this._adapter.focusTabAtIndex(index);
                }
            }
        }
        else {
            var focusedTabIndex = this._adapter.getFocusedTabIndex();
            if (focusedTabIndex === -1) {
                return;
            }
            if (this._isActivationKey(key)) {
                var activateEventData = { index: focusedTabIndex };
                var canActivate = this._adapter.emitHostEvent(TAB_BAR_CONSTANTS.events.ACTIVATE, activateEventData, true, true);
                if (canActivate) {
                    this.activateTab(focusedTabIndex);
                }
            }
            else {
                var index = this._determineTargetFromKey(focusedTabIndex, key);
                if (focusedTabIndex === index) {
                    return;
                }
                this._adapter.focusTabAtIndex(index);
                this.scrollIntoView(index);
            }
        }
    };
    TabBarFoundation.prototype._onScrollPrevious = function (evt) {
        var scrollAmount = -this._adapter.getOffsetWidth();
        this._adapter.incrementScroll(scrollAmount);
        this._syncScrollButtons();
    };
    TabBarFoundation.prototype._onScrollNext = function (evt) {
        var scrollAmount = this._adapter.getOffsetWidth();
        this._adapter.incrementScroll(scrollAmount);
        this._syncScrollButtons();
    };
    TabBarFoundation.prototype._onScroll = function (evt) {
        this._syncScrollButtons();
    };
    TabBarFoundation.prototype._syncScrollButtons = function () {
        var isScrollable = this._isScrollable();
        this._adapter.setPreviousButtonVisibility(isScrollable);
        this._adapter.setNextButtonVisibility(isScrollable);
        if (isScrollable) {
            this._adapter.setPreviousButtonEnabled(this._adapter.isScrolled());
            this._adapter.setNextButtonEnabled(!this._adapter.isScrolledEnd());
        }
    };
    /**
     * Determines the index of the adjacent tab closest to either edge of the Tab Bar
     * @param index The index of the tab
     * @param tabDimensions The dimensions of the tab
     * @param scrollPosition The current scroll position
     * @param barWidth The width of the tab bar
     */
    TabBarFoundation.prototype._findAdjacentTabIndexClosestToEdge = function (index, tabDimensions, scrollPosition, barWidth) {
        var relativeRootLeft = tabDimensions.rootLeft - scrollPosition;
        var relativeRootRight = tabDimensions.rootRight - scrollPosition - barWidth;
        var relativeRootDelta = relativeRootLeft + relativeRootRight;
        var leftEdgeIsCloser = relativeRootLeft < 0 || relativeRootDelta < 0;
        var rightEdgeIsCloser = relativeRootRight > 0 || relativeRootDelta > 0;
        if (leftEdgeIsCloser) {
            return index - 1;
        }
        if (rightEdgeIsCloser) {
            return index + 1;
        }
        return -1;
    };
    /**
     * Calculates the scroll increment that will make the tab at the given index visible
     * @param index The index of the tab
     * @param nextIndex The index of the next tab
     * @param scrollPosition The current scroll position
     * @param barWidth The width of the tab-bar
     */
    TabBarFoundation.prototype._calculateScrollIncrement = function (index, nextIndex, scrollPosition, barWidth) {
        var nextTabDimensions = this._adapter.getTabDimensionsAtIndex(nextIndex);
        var relativeContentLeft = nextTabDimensions.contentLeft - scrollPosition - barWidth;
        var relativeContentRight = nextTabDimensions.contentRight - scrollPosition;
        var leftIncrement = relativeContentRight - TAB_BAR_CONSTANTS.numbers.EXTRA_SCROLL_AMOUNT;
        var rightIncrement = relativeContentLeft + TAB_BAR_CONSTANTS.numbers.EXTRA_SCROLL_AMOUNT;
        if (nextIndex < index) {
            return Math.min(leftIncrement, 0);
        }
        return Math.max(rightIncrement, 0);
    };
    /**
     * Private method for determining the index of the destination tab based on what key was pressed
     * @param origin The original index from which to determine the destination
     * @param key The name of the key
     */
    TabBarFoundation.prototype._determineTargetFromKey = function (origin, key, nextIndex) {
        if (origin === nextIndex) {
            return origin;
        }
        var maxIndex = this._adapter.getTabCount() - 1;
        var shouldGoToEnd = key === TAB_BAR_CONSTANTS.strings.END_KEY;
        var shouldDecrement = key === TAB_BAR_CONSTANTS.strings.ARROW_LEFT_KEY;
        var shouldIncrement = key === TAB_BAR_CONSTANTS.strings.ARROW_RIGHT_KEY;
        var index = nextIndex !== undefined ? nextIndex : origin;
        if (shouldGoToEnd) {
            index = maxIndex;
        }
        else if (shouldDecrement) {
            index -= 1;
        }
        else if (shouldIncrement) {
            index += 1;
        }
        else {
            index = 0;
        }
        if (index < 0) {
            index = maxIndex;
        }
        else if (index > maxIndex) {
            index = 0;
        }
        // If this tab is disabled, then we'll try again...
        if (this._adapter.isTabDisabled(index)) {
            return this._determineTargetFromKey(origin, key, index);
        }
        return index;
    };
    TabBarFoundation.prototype._isScrollable = function () {
        return this._forceScrollButtons ||
            (this._scrollButtons &&
                !Platform.isMobile &&
                !this._isFixed &&
                this._adapter.getScrollContentWidth() > this._adapter.getOffsetWidth());
    };
    TabBarFoundation.prototype._getKeyFromEvent = function (evt) {
        if (ACCEPTABLE_KEYS.includes(evt.key)) {
            return evt.key;
        }
        return KEYCODE_MAP[evt.keyCode];
    };
    TabBarFoundation.prototype._isActivationKey = function (key) {
        return key === TAB_BAR_CONSTANTS.strings.SPACE_KEY || key === TAB_BAR_CONSTANTS.strings.ENTER_KEY;
    };
    TabBarFoundation.prototype._indexIsInRange = function (index) {
        return index >= 0 && index < this._adapter.getTabCount();
    };
    TabBarFoundation.prototype.activateTab = function (index) {
        if (!isNumber(index)) {
            index = -1;
        }
        // If we have a currently selected tab we need to deactivate that now
        if (this._activeTab !== -1) {
            this._adapter.deactivateTab(this._activeTab);
        }
        // Activate the requested tab
        var previousTabBounds = this._adapter.getTabBounds(this._activeTab);
        this._adapter.activateTab(index, previousTabBounds);
        this._adapter.syncTabIndex(index);
        this._adapter.ensureFocusableTab();
        this.scrollIntoView(index);
        this._activeTab = index;
        this._syncScrollButtons();
        this._adapter.setHostAttribute(TAB_BAR_CONSTANTS.attributes.ACTIVE_TAB, this._activeTab.toString());
    };
    TabBarFoundation.prototype.scrollIntoView = function (index) {
        if (!this._indexIsInRange(index)) {
            return;
        }
        if (index === 0) {
            return this._adapter.scrollTo(0);
        }
        if (index === this._adapter.getTabCount() - 1) {
            return this._adapter.scrollTo(this._adapter.getScrollContentWidth());
        }
        var scrollPosition = this._adapter.getScrollPosition();
        var barWidth = this._adapter.getOffsetWidth();
        var tabDimensions = this._adapter.getTabDimensionsAtIndex(index);
        var nextIndex = this._findAdjacentTabIndexClosestToEdge(index, tabDimensions, scrollPosition, barWidth);
        if (!this._indexIsInRange(nextIndex)) {
            return;
        }
        var scrollIncrement = this._calculateScrollIncrement(index, nextIndex, scrollPosition, barWidth);
        this._adapter.incrementScroll(scrollIncrement);
    };
    Object.defineProperty(TabBarFoundation.prototype, "activeTab", {
        /** Gets and sets the active tab index. */
        get: function () {
            return this._activeTab;
        },
        set: function (value) {
            if (this._activeTab !== value) {
                if (!this._isInitialized) {
                    this._activeTab = value;
                    this._adapter.setHostAttribute(TAB_BAR_CONSTANTS.attributes.ACTIVE_TAB, this._activeTab.toString());
                }
                else {
                    this.activateTab(value);
                }
            }
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(TabBarFoundation.prototype, "_isFixed", {
        get: function () {
            return this._layoutMode === 'fixed';
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(TabBarFoundation.prototype, "layoutMode", {
        get: function () {
            return this._layoutMode;
        },
        set: function (value) {
            if (this._layoutMode !== value) {
                this._layoutMode = value;
                this._adapter.setLayoutMode(this._layoutMode);
                this._syncScrollButtons();
            }
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(TabBarFoundation.prototype, "layoutAlign", {
        get: function () {
            return this._layoutAlign;
        },
        set: function (value) {
            if (this._layoutAlign !== value) {
                this._layoutAlign = value;
                this._adapter.setLayoutAlign(this._layoutAlign);
            }
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(TabBarFoundation.prototype, "underline", {
        get: function () {
            return this._underline;
        },
        set: function (value) {
            if (this._underline !== value) {
                this._underline = value;
                this._adapter.setUnderline(this._underline);
                this._adapter.toggleHostAttribute(TAB_BAR_CONSTANTS.attributes.UNDERLINE, this._underline);
            }
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(TabBarFoundation.prototype, "stacked", {
        get: function () {
            return this._stacked;
        },
        set: function (value) {
            if (this._stacked !== value) {
                this._stacked = value;
                this._adapter.setStacked(this._stacked);
                if (this._stacked) {
                    this._adapter.setHostAttribute(TAB_BAR_CONSTANTS.attributes.STACKED);
                }
                else {
                    this._adapter.removeHostAttribute(TAB_BAR_CONSTANTS.attributes.STACKED);
                }
            }
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(TabBarFoundation.prototype, "scrollButtons", {
        get: function () {
            return this._scrollButtons;
        },
        set: function (value) {
            if (this._scrollButtons !== value) {
                this._scrollButtons = value;
                this._adapter.setHostAttribute(TAB_BAR_CONSTANTS.attributes.SCROLL_BUTTONS, this._scrollButtons.toString());
                if (this._isInitialized) {
                    this._syncScrollButtons();
                }
            }
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(TabBarFoundation.prototype, "forceScrollButtons", {
        get: function () {
            return this._forceScrollButtons;
        },
        set: function (value) {
            if (this._forceScrollButtons !== value) {
                this._forceScrollButtons = value;
                if (this._isInitialized) {
                    this._syncScrollButtons();
                }
            }
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(TabBarFoundation.prototype, "focusOnActivate", {
        get: function () {
            return this._focusOnActivate;
        },
        set: function (value) {
            if (this._focusOnActivate !== value) {
                this._focusOnActivate = value;
                this._adapter.setFocusOnActivate(this._focusOnActivate);
                this._adapter.setHostAttribute(TAB_BAR_CONSTANTS.attributes.FOCUS_ON_ACTIVATE, this._focusOnActivate.toString());
            }
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(TabBarFoundation.prototype, "autoActivate", {
        get: function () {
            return this._autoActivate;
        },
        set: function (value) {
            if (this._autoActivate !== value) {
                this._autoActivate = value;
                this._adapter.setHostAttribute(TAB_BAR_CONSTANTS.attributes.AUTO_ACTIVATE, this._autoActivate.toString());
            }
        },
        enumerable: true,
        configurable: true
    });
    return TabBarFoundation;
}());
export { TabBarFoundation };
